import { BigNumber, BigNumberish } from 'ethers';
import memoizee from 'memoizee';

import { NftTokenOption } from '../../components/Web3/NftPrintService/ImagePicker/ImagePicker';
import { MINUTE, SECOND } from '../../constants/dateTime';
import { GeneratedNftPrint } from '../../types/generated';
import { NFTMetadata, NftTokenInfo } from '../../types/web3/nft';

const DATA_URI_PRECURSUR = 'data:';
const IPFS_PRECURSUR = 'ipfs://';
const IPFS_GATEWAY = 'https://cloudflare-ipfs.com/ipfs/'; // https://ipfs.io/ipfs/
const AA_THUMBNAIL_IMAGE_BASE_URL =
  'https://storage.googleapis.com/avantarte-nft-thumbnails-default/';
const maxReqTimeout = 10 * SECOND;

async function fetchWithTimeout(
  timeout: number,
  input: RequestInfo | URL,
  init?: Omit<RequestInit, 'signal'>,
) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);
  try {
    const response = await fetch(
      input as RequestInfo,
      {
        ...init,
        signal: controller.signal,
      } as RequestInit,
    );
    clearTimeout(id);
    return response;
  } catch (err) {
    clearTimeout(id);
    throw err;
  }
}

export const nftMetadataToStoryblockImage = (
  metadata: NFTMetadata,
  tokenId: BigNumberish,
  contractAddress: string,
): ProductImageComponent => {
  const token = BigNumber.from(tokenId);
  return {
    _uid: `${contractAddress}/${token.toString()}`,
    component: 'image',
    image: {
      id: token.toNumber(),
      alt: metadata.description || `Token ${token.toString()}`,
      name: metadata.name || token.toString(),
      title: metadata.name || token.toString(),
      filename: metadata.image ? normalizeTokenURI(metadata.image) : '',
      copyright: '',
      fieldtype: '',
    },
    showCopyright: false,
    caption: metadata.name,
  };
};

export const buildNftImageUrls = (
  contractAddress: string,
  tokenId: string,
  printMatchesNft: boolean,
) => {
  const basePath = `${AA_THUMBNAIL_IMAGE_BASE_URL}${contractAddress}`;
  const tokenImage = `${tokenId}.png`;

  return {
    nft: `${basePath}/nft/${tokenImage}`,
    print: !printMatchesNft && `${basePath}/print/${tokenImage}`,
    url: `${basePath}/${tokenImage}`,
  };
};

export const nftTokenToPrintOption = (
  tokens: NftTokenInfo[],
  printed: string[],
  contractAddress: string,
): NftTokenOption[] => {
  return tokens.map(({ id }) => ({
    disabled: printed.indexOf(id) !== -1,
    id,
    imageUris: buildNftImageUrls(contractAddress, id, false),
  }));
};

export const generatedNftToTokenPrinted = (
  printed: GeneratedNftPrint[],
  contractAddress: string,
  printMatchesNft: boolean,
) => {
  return printed.map((printedToken) => ({
    ...printedToken,
    printed: true,
    imageUris: buildNftImageUrls(
      contractAddress,
      printedToken.tokenId,
      printMatchesNft,
    ),
  }));
};

// checks if a URI is a data URI
export const isDataUri = (s: string) => s.startsWith(DATA_URI_PRECURSUR);
// checks if a URI is an IPFS URI
export const isIpfsUri = (s: string) => s.startsWith(IPFS_PRECURSUR);

export const normalizeTokenURI = (uri: string) => {
  // normalize IPFS uris into http uris
  if (isIpfsUri(uri)) {
    return uri.replace(IPFS_PRECURSUR, IPFS_GATEWAY);
  }
  return uri;
};

// gets the manifest from data URI
export const getManifestFromDataUri = (s: string): NFTMetadata => {
  const [_description, base64] = s.split(',');
  const jsonString = Buffer.from(base64, 'base64').toString('utf8');
  return JSON.parse(jsonString);
};

// handles a uri from the blockchain and fetches it's metadata
export const getNftMetadataFromUri = memoizee(
  async (uri: string) => {
    if (isDataUri(uri)) {
      return getManifestFromDataUri(uri);
    }
    const metadataResponse = await fetchWithTimeout(
      maxReqTimeout,
      normalizeTokenURI(uri),
      {
        cache: 'force-cache',
      },
    );

    const json = (await metadataResponse.json()) as NFTMetadata;
    json.image = json.image ? normalizeTokenURI(json.image) : '';
    return json;
  },
  { length: 1, primitive: true, promise: true, max: 1000, maxAge: MINUTE },
);
